import {
    CircularProgress,
    Dialog,
    IconButton,
    Switch,
    TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect, useMemo } from "react";
import { ShieldCheck } from "lucide-react";
import {
    getKycPlanByIdAction,
    createKycPlanAction,
    updateKycPlanAction,
} from "../../Actions/KycPlanManagemantAction";
import { MyCloseIcon } from "../../Assets/svg/MyIcons";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import { getPlanFeatureListAction } from "../../Actions/PlanFeatureAction";
import { containsBadWords } from "../../utils/badWord";
import "../../components/Common/FormModal.css";
import "./AddEditKycPlan.css";

const { VALIDATION } = FRONTEND_MESSAGES;

const addonSchema = Yup.object({
    is_enabled: Yup.boolean(),
    unit_price: Yup.number()
        .typeError("Unit price must be a number")
        .when("is_enabled", {
            is: true,
            then: (s) =>
                s.required("Unit price is required").positive("Price must be positive"),
            otherwise: (s) => s.notRequired().nullable()
        })
});

const validationSchema = Yup.object({
    name: Yup.string()
        .trim()
        .matches(/^[a-zA-Z0-9\s&-]+$/, VALIDATION.INVALID_FORMAT("Plan name"))
        .max(50, VALIDATION.MAX_LENGTH("Plan name", 50))
        .required(VALIDATION.REQUIRED_FIELD("Plan name"))
        .test(
            "bad-word",
            "Plan Name contains inappropriate language.",
            (value) => !containsBadWords(value || "")
        ),
    description: Yup.string()
        .trim()
        .matches(/^[a-zA-Z0-9\s&.,()-]+$/, VALIDATION.INVALID_FORMAT("Description"))
        .max(500, VALIDATION.MAX_LENGTH("Description", 500))
        .required(VALIDATION.REQUIRED_FIELD("Description"))
        .test(
            "bad-word",
            "Plan Description contains inappropriate language.",
            (value) => !containsBadWords(value || "")
        ),
    is_kyc_enabled: Yup.boolean(),
    is_aml_enabled: Yup.boolean(),
    is_selfie_enabled: Yup.boolean(),
    is_ongoing_aml_enabled: Yup.boolean(),
    is_portal_enabled: Yup.boolean().test(
        "portal-check",
        "At least one of KYC or AML must be enabled",
        function (value) {
            const { portal_kyc_enabled, portal_aml_enabled } = this.parent;

            if (!value) return true;

            return portal_kyc_enabled || portal_aml_enabled;
        }
    ),
    portal_kyc_enabled: Yup.boolean(),
    portal_selfie_enabled: Yup.boolean(),
    portal_aml_enabled: Yup.boolean(),
    monthly_base_price: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Base price"))
        .positive(VALIDATION.POSITIVE_ONLY("Base price"))
        .max(999999999, VALIDATION.MAX_VALUE("Base price", 999999999))
        .required(VALIDATION.REQUIRED_FIELD("Base price")),
    monthly_included_verification: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Included verifications"))
        .min(0, VALIDATION.MIN_VALUE("Included verifications", 0))
        .required(VALIDATION.REQUIRED_FIELD("Included verifications")),
    yearly_base_price: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Base price"))
        .positive(VALIDATION.POSITIVE_ONLY("Base price"))
        .max(999999999, VALIDATION.MAX_VALUE("Base price", 999999999))
        .required(VALIDATION.REQUIRED_FIELD("Base price")),
    yearly_included_verification: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Included verifications"))
        .min(0, VALIDATION.MIN_VALUE("Included verifications", 0))
        .required(VALIDATION.REQUIRED_FIELD("Included verifications")),
    per_verification_price: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Per verification price"))
        .positive(VALIDATION.POSITIVE_ONLY("Per verification price"))
        .max(999999999, VALIDATION.MAX_VALUE("Per verification price", 999999999))
        .required(VALIDATION.REQUIRED_FIELD("Per verification price")),
    is_custom: Yup.boolean(),
    isActive: Yup.boolean(),
    addons: Yup.object({
        aml_match: addonSchema,
        aml_ongoing_check: addonSchema
    })
});

// Helper: Compare old vs new values (Deep comparison)
const getChangedPlanFields = (oldData, newData) => {
    const changes = {};
    if (!oldData) return changes;

    Object.keys(newData).forEach((key) => {
        const oldVal = oldData[key];
        const newVal = newData[key];

        const oldIsEmpty = oldVal === null || oldVal === undefined || oldVal === "";
        const newIsEmpty = newVal === null || newVal === undefined || newVal === "";

        if (oldIsEmpty && newIsEmpty) return;

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changes[key] = {
                old: oldVal,
                new: newVal,
            };
        }
    });

    return changes;
};


const PriceBadge = ({ price }) =>
    price && price > 0 ? (
        <span className="kyc-plan-price-badge">+${price}</span>
    ) : null;

const AddEditKycPlan = ({
    open,
    handleClose,
    id,
    refetchPlanList = () => { },
    handleToast = () => { },
    changePortalFilter,
    featureData,
}) => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState(null);
    const [planData, setPlanData] = useState(null);
    const [changedFields, setChangedFields] = useState({});
    const [planPricingData, setPlanPricingData] = useState([]);
    const [pricingLoading, setPricingLoading] = useState(false);
    const [extraFeatures, setExtraFeatures] = useState(0);

    const formik = useFormik({
        initialValues: {
            name: "",
            description: "",
            is_kyc_enabled: false,
            is_aml_enabled: false,
            is_selfie_enabled: false,
            is_ongoing_aml_enabled: false,
            is_portal_enabled: false,
            portal_kyc_enabled: false,
            portal_selfie_enabled: false,
            portal_aml_enabled: false,
            monthly_base_price: "",
            monthly_included_verification: "",
            yearly_base_price: "",
            yearly_included_verification: "",
            per_verification_price: "",
            is_custom: false,
            isActive: true,
            addons: {
                aml_match: {
                    is_enabled: false,
                    unit_price: ""
                },
                aml_ongoing_check: {
                    is_enabled: false,
                    unit_price: ""
                }
            }
        },
        validationSchema,
        validate: (values) => {
            const errors = {};

            if (
                values.is_portal_enabled &&
                !values.portal_kyc_enabled &&
                !values.portal_aml_enabled
            ) {
                errors.is_portal_enabled = "At least one of KYC or AML must be enabled";
            }

            return errors;
        },
        onSubmit: async (values) => {
            const addonsArray = [
                {
                    addon_type: "aml_match",
                    unit_price: values.addons.aml_match.unit_price || 0,
                    feature_snapshot: getFeaturePrice("aml_match") || 0,
                    is_enabled: values.addons.aml_match.is_enabled
                },
                {
                    addon_type: "aml_ongoing_check",
                    unit_price: values.addons.aml_ongoing_check.unit_price || 0,
                    feature_snapshot: getFeaturePrice("aml_ongoing_check") || 0,
                    is_enabled: values.addons.aml_ongoing_check.is_enabled
                }
            ];

            const featureSnapshot = [];

            const addFeature = (label, enabled, charge) => {
                if (!enabled) return;

                featureSnapshot.push({
                    label,
                    enabled: true,
                    charge: Number(charge || 0),
                });
            };

            // Base priced features
            addFeature(
                "KYC Enabled",
                values.is_kyc_enabled,
                getFeaturePrice("is_kyc_enabled")
            );

            addFeature(
                "AML Enabled",
                values.is_aml_enabled,
                getFeaturePrice("is_aml_enabled")
            );

            addFeature(
                "Ongoing AML",
                values.is_ongoing_aml_enabled,
                getFeaturePrice("is_ongoing_aml_enabled")
            );

            addFeature(
                "Selfie Enabled",
                values.is_selfie_enabled,
                getFeaturePrice("is_selfie_enabled")
            );

            addFeature(
                "Portal Enabled",
                values.is_portal_enabled,
                getFeaturePrice("is_portal_enabled")
            );

            addFeature(
                "Portal KYC",
                values.portal_kyc_enabled,
                getFeaturePrice("portal_kyc_enabled")
            );

            addFeature(
                "Portal AML",
                values.portal_aml_enabled,
                getFeaturePrice("portal_aml_enabled")
            );

            addFeature(
                "Portal Selfie",
                values.portal_selfie_enabled,
                getFeaturePrice("portal_selfie_enabled")
            );

            // Addons
            if (values.addons.aml_match.is_enabled) {
                featureSnapshot.push({
                    label: "AML Match",
                    enabled: true,
                    charge: Number(values.addons.aml_match.unit_price || 0),
                });
            }

            if (values.addons.aml_ongoing_check.is_enabled) {
                featureSnapshot.push({
                    label: "AML Ongoing Check",
                    enabled: true,
                    charge: Number(values.addons.aml_ongoing_check.unit_price || 0),
                });
            }

            const payload = {
                ...values,
                name: values.name?.trim(),
                description: values.description?.trim(),
                addons: addonsArray,
                isExtraCharges: extraFeatures,
                feature_snapshot: featureSnapshot,
            };

            if (id) {
                payload.changedFields = JSON.stringify(Object.keys(changedFields));
                payload.comparisonData = JSON.stringify(changedFields);
            }

            setLoading(true);
            try {
                if (id) {
                    const response = await updateKycPlanAction(id, payload);
                    const { message } = response?.data || {};
                    handleToast("success", message);
                } else {
                    const response = await createKycPlanAction(payload);
                    const { message } = response?.data || {};
                    changePortalFilter("kyc");
                    handleToast("success", message);
                }
                handleClose();
            } catch (err) {
                const message =
                    err?.response?.data?.message ||
                    err?.message ||
                    (id
                        ? "Something went wrong while updating plan"
                        : "Something went wrong while adding plan");

                handleToast("error", message);
                console.error("❌ Error saving plan:", err);
            } finally {
                setLoading(false);
                refetchPlanList();
            }
        },
    });

    useEffect(() => {
        const fetchPlan = async () => {
            if (!id) return;
            setFetching(true);
            try {
                const response = await getKycPlanByIdAction(id);
                const data = response?.data?.data || {};

                setPlanData(data);
                
                const addonMap = {
                    aml_match: { is_enabled: false, unit_price: "" },
                    aml_ongoing_check: { is_enabled: false, unit_price: "" }
                };

                data?.addons?.forEach(addon => {
                    if (addonMap[addon.addon_type]) {
                        addonMap[addon.addon_type] = {
                            is_enabled: addon.is_enabled,
                            unit_price: addon.is_enabled ? addon.unit_price : ""
                        };
                    }
                });

                const initialValuesToSet = {
                    name: data.name || "",
                    description: data.description || "",
                    is_kyc_enabled: data.is_kyc_enabled ?? false,
                    is_aml_enabled: data.is_aml_enabled ?? false,
                    is_selfie_enabled: data.is_selfie_enabled ?? false,
                    is_ongoing_aml_enabled: data.is_ongoing_aml_enabled ?? false,
                    is_portal_enabled: data.is_portal_enabled ?? false,
                    portal_kyc_enabled: data.portal_kyc_enabled ?? false,
                    portal_selfie_enabled: data.portal_selfie_enabled ?? false,
                    portal_aml_enabled: data.portal_aml_enabled ?? false,
                    monthly_base_price: data.monthly_base_price ?? "",
                    monthly_included_verification: data.monthly_included_verification ?? "",
                    yearly_base_price: data.yearly_base_price ?? "",
                    yearly_included_verification: data.yearly_included_verification ?? "",
                    per_verification_price: data.per_verification_price ?? "",
                    is_custom: data.is_custom ?? false,
                    isActive: data.isActive ?? true,
                    addons: addonMap
                };

                formik.resetForm({
                    values: initialValuesToSet,
                });
                setInitialFormValues(initialValuesToSet);

            } catch (err) {
                console.error("❌ Error fetching plan:", err);
            } finally {
                setFetching(false);
            }
        };
        fetchPlan();
    }, [id]);

    // Track changed fields for audit/update logging
    useEffect(() => {
        if (!id || !initialFormValues) return;

        const changed = getChangedPlanFields(initialFormValues, formik.values);
        setChangedFields(changed);
    }, [formik.values, initialFormValues, id]);

    useEffect(() => {
        const fetchPricing = async () => {
            setPricingLoading(true);
            try {
                const response = await getPlanFeatureListAction({
                    portal_name: "kyc",
                });

                const features = response?.data?.data || [];
                setPlanPricingData(features);
            } catch (err) {
                console.error("Failed to load KYC pricing:", err);
                setPlanPricingData([]);
            } finally {
                setPricingLoading(false);
            }
        };

        fetchPricing();
    }, []);

    const handleTextChange = (e) => {
        formik.handleChange(e);
        formik.setFieldTouched(e.target.name, true, false);
    };

    const handleNumberChange = (e) => {
        const value = e.target.value.replace(/\s/g, '');
        formik.setFieldValue(e.target.name, value);
        formik.setFieldTouched(e.target.name, true, false);
    };

    const handleToggle = (key) => (e) => {
        const value = e.target.checked;

        formik.setFieldValue(key, value, true);

        // Portal logic
        if (key === "is_portal_enabled" && !value) {
            formik.setFieldValue("portal_kyc_enabled", false);
            formik.setFieldValue("portal_aml_enabled", false);
            formik.setFieldValue("portal_selfie_enabled", false);
        }

        // Portal KYC logic
        if (key === "portal_kyc_enabled" && !value) {
            formik.setFieldValue("portal_selfie_enabled", false);
        }

        // KYC logic
        if (key === "is_kyc_enabled" && !value) {
            formik.setFieldValue("is_aml_enabled", false);
            formik.setFieldValue("is_ongoing_aml_enabled", false);
            formik.setFieldValue("is_selfie_enabled", false);
        }

        // AML logic
        if (key === "is_aml_enabled" && !value) {
            formik.setFieldValue("is_ongoing_aml_enabled", false);
        }
    }


    const hasFeatureError = useMemo(() => {
        const allowedFeaturesValues = formik.values.allowedFeatures || {};
        const hasAtLeastOneFeature = allowedFeaturesValues.user_chat === true || allowedFeaturesValues.support_tickets === true;
        const hasError = formik.errors.allowedFeatures;
        return !hasAtLeastOneFeature && hasError
            ? "error.main"
            : "rgba(25, 118, 210, 0.12)";
    }, [formik.values.allowedFeatures, formik.errors.allowedFeatures]);


    // Use featureData prop (already available) instead of a separate API fetch
    const getFeaturePrice = (key) => {
        const source = featureData && Array.isArray(featureData) && featureData.length
            ? featureData
            : planPricingData;
        if (!source || !Array.isArray(source)) return undefined;
        const feature = source.find(f => f.feature_name === key);
        return feature ? parseFloat(feature.price) : undefined;
    };

    const FEATURE_LABELS = {
        is_kyc_enabled: "KYC Enabled",
        is_aml_enabled: "AML Enabled",
        is_selfie_enabled: "Selfie Enabled",
        is_ongoing_aml_enabled: "Ongoing AML",
        is_portal_enabled: "Portal Enabled",
        portal_kyc_enabled: "Portal KYC",
        portal_selfie_enabled: "Portal Selfie",
        portal_aml_enabled: "Portal AML",
        aml_match: "AML Match",
        aml_ongoing_check: "AML Ongoing Check",
    };

    const oldChargeMap = {};
    (planData?.feature_snapshot || []).forEach((f) => {
        if (f?.label) {
            oldChargeMap[f.label] = Number(f.charge) || 0;
        }
    });

    const getPriceForBadge = (key) => {
        const newPrice = getFeaturePrice(key);
        if (newPrice == null || Number(newPrice) === 0) return null;

        const label = FEATURE_LABELS[key];
        const oldPrice = oldChargeMap[label];

        if (id && oldPrice != null && Number(oldPrice) !== Number(newPrice)) {
            return {
                changed: true,
                text: `$${Number(oldPrice)} → $${Number(newPrice)}`,
            };
        }

        return {
            changed: false,
            value: Number(newPrice),
        };
    };

    // Calculate which features are enabled and their total cost
    const calculateSelectedPrices = () => {
        const source = featureData && Array.isArray(featureData) && featureData.length
            ? featureData
            : planPricingData;

        const selected = [];
        let totalPrice = 0;

        // Base feature keys to check
        const baseFeatureKeys = [
            "is_kyc_enabled",
            "is_aml_enabled",
            "is_selfie_enabled",
            "is_ongoing_aml_enabled",
            "is_portal_enabled",
            "portal_kyc_enabled",
            "portal_selfie_enabled",
            "portal_aml_enabled",
        ];

        baseFeatureKeys.forEach((key) => {
            if (formik.values[key]) {
                const row = source?.find(f => f.feature_name === key);
                const price = row ? parseFloat(row.price) : 0;
                if (price > 0) {
                    selected.push({
                        name: key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
                        price,
                    });
                    totalPrice += price;
                }
            }
        });

        // Addon keys (level 2, parent = "addons" row)
        const addonKeys = ["aml_match", "aml_ongoing_check"];
        addonKeys.forEach((key) => {
            if (formik.values.addons?.[key]?.is_enabled) {
                const row = source?.find(f => f.feature_name === key && f.level === 2);
                const price = row ? parseFloat(row.price) : 0;
                if (price > 0) {
                    selected.push({
                        name: key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
                        price,
                        isAddon: true,
                    });
                    totalPrice += price;
                }
            }
        });

        return { selected, totalPrice };
    };

    const { selected: selectedItems, totalPrice } = calculateSelectedPrices();

    const isExtraChargeUpdated =
        id &&
        planData?.isExtraCharges != null &&
        Number(planData.isExtraCharges) !== Number(totalPrice);

    // Console log pricing whenever selections change
    useEffect(() => {
        const { selected: items, totalPrice: total } = calculateSelectedPrices();
        if (!items.length) return;
        setExtraFeatures(total);
    }, [
        formik.values.is_kyc_enabled,
        formik.values.is_aml_enabled,
        formik.values.is_selfie_enabled,
        formik.values.is_ongoing_aml_enabled,
        formik.values.is_portal_enabled,
        formik.values.portal_kyc_enabled,
        formik.values.portal_selfie_enabled,
        formik.values.portal_aml_enabled,
        formik.values.addons?.aml_match?.is_enabled,
        formik.values.addons?.aml_ongoing_check?.is_enabled,
    ]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ className: "fm-dialog kyc-plan-dialog" }}
        >
            <div className="fm-header">
                <span className="fm-header-icon" aria-hidden>
                    <ShieldCheck size={20} />
                </span>
                <div className="fm-header-copy">
                    <h2>
                        {id
                            ? "Update KYC Subscription Plan"
                            : "Create KYC Subscription Plan"}
                    </h2>
                    <p>
                        {id
                            ? "Update plan details, pricing and features."
                            : "Configure pricing, billing and available features."}
                    </p>
                </div>
                <IconButton
                    aria-label="close"
                    className="fm-close"
                    onClick={handleClose}
                >
                    <MyCloseIcon />
                </IconButton>
            </div>

            {fetching ? (
                <div className="kyc-plan-loading">
                    <CircularProgress size={32} />
                </div>
            ) : (
                <form onSubmit={formik.handleSubmit} className="fm-form">
                    <div className="fm-body">
                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Plan information</h3>
                                <p>Name and description shown to subscribers.</p>
                            </div>
                            <div className="fm-card">
                                <div className="fm-grid is-stack">
                                    <div className="fm-field">
                                        <label htmlFor="kyc-plan-name">
                                            Plan Name
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="kyc-plan-name"
                                            placeholder="Enter plan name"
                                            name="name"
                                            size="small"
                                            fullWidth
                                            disabled={id}
                                            value={formik.values.name}
                                            onChange={handleTextChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.name &&
                                                Boolean(formik.errors.name)
                                            }
                                            helperText={
                                                formik.touched.name &&
                                                formik.errors.name
                                            }
                                        />
                                    </div>
                                    <div className="fm-field">
                                        <label htmlFor="kyc-plan-description">
                                            Description
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="kyc-plan-description"
                                            placeholder="Enter description"
                                            name="description"
                                            size="small"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={formik.values.description}
                                            onChange={handleTextChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.description &&
                                                Boolean(formik.errors.description)
                                            }
                                            helperText={
                                                formik.touched.description &&
                                                formik.errors.description
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="fm-section">
                            <div className="kyc-plan-split">
                                <div>
                                    <div className="fm-section-head">
                                        <h3>Custom plan</h3>
                                        <p>Standard vs custom plan type.</p>
                                    </div>
                                    <div className="fm-card">
                                        <div className="fm-toggle-list">
                                            <label
                                                className={`fm-toggle${
                                                    formik.values.is_custom
                                                        ? " is-on"
                                                        : ""
                                                }`}
                                            >
                                                <span>
                                                    <p className="fm-toggle-title">
                                                        {formik.values.is_custom
                                                            ? "Custom"
                                                            : "Standard"}
                                                    </p>
                                                    <p className="fm-toggle-sub">
                                                        {formik.values.is_custom
                                                            ? "custom plan"
                                                            : "standard plan with predefined features and limits"}
                                                    </p>
                                                </span>
                                                <Switch
                                                    checked={formik.values.is_custom}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(
                                                            "is_custom",
                                                            e.target.checked
                                                        )
                                                    }
                                                    color="primary"
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="fm-section-head">
                                        <h3>Plan status</h3>
                                        <p>Visibility for subscribers.</p>
                                    </div>
                                    <div className="fm-card">
                                        <div className="fm-toggle-list">
                                            <label
                                                className={`fm-toggle${
                                                    formik.values.isActive
                                                        ? " is-on"
                                                        : ""
                                                }`}
                                            >
                                                <span>
                                                    <p className="fm-toggle-title">
                                                        {formik.values.isActive
                                                            ? "Active"
                                                            : "Inactive"}
                                                    </p>
                                                    <p className="fm-toggle-sub">
                                                        {formik.values.isActive
                                                            ? "This plan is available for users to subscribe"
                                                            : "This plan is hidden from users"}
                                                    </p>
                                                </span>
                                                <Switch
                                                    checked={formik.values.isActive}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(
                                                            "isActive",
                                                            e.target.checked
                                                        )
                                                    }
                                                    color="primary"
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Base features</h3>
                                <p>Core KYC, AML, and selfie capabilities.</p>
                            </div>
                            <div className="fm-card">
                                <div
                                    className={`kyc-feature-tree${
                                        hasFeatureError === "error.main"
                                            ? " is-error"
                                            : ""
                                    }`}
                                >
                                    <div
                                        className={`kyc-feature-card${
                                            formik.values.is_kyc_enabled
                                                ? " is-on"
                                                : ""
                                        }`}
                                    >
                                        <span className="kyc-feature-label">
                                            KYC Enabled
                                                {(() => {
                                                    const info = getPriceForBadge("is_kyc_enabled");
                                                    if (!info) return null;

                                                    if (info.changed) {
                                                        return (
                                                            <span className="kyc-plan-price-badge">
                                                                +{info.text}
                                                            </span>
                                                        );
                                                    }
                                                    return <PriceBadge price={info.value} />;
                                                })()}
                                        </span>
                                        <Switch
                                            checked={formik.values.is_kyc_enabled}
                                            onChange={handleToggle("is_kyc_enabled")}
                                        />
                                    </div>

                                    {formik.values.is_kyc_enabled && (
                                        <div className="kyc-feature-nest">
                                            <div
                                                className={`kyc-feature-card${
                                                    formik.values.is_aml_enabled
                                                        ? " is-on"
                                                        : ""
                                                }`}
                                            >
                                                <span className="kyc-feature-label">
                                                    AML Enabled
                                                        {(() => {
                                                            const info = getPriceForBadge("is_aml_enabled");
                                                            if (!info) return null;

                                                            if (info.changed) {
                                                                return (
                                                                    <span className="kyc-plan-price-badge">
                                                                        +{info.text}
                                                                    </span>
                                                                );
                                                            }
                                                            return <PriceBadge price={info.value} />;
                                                        })()}
                                                </span>
                                                <Switch
                                                    checked={
                                                        formik.values.is_aml_enabled
                                                    }
                                                    onChange={handleToggle(
                                                        "is_aml_enabled"
                                                    )}
                                                />
                                            </div>

                                            {formik.values.is_aml_enabled && (
                                                <div className="kyc-feature-nest">
                                                    <div
                                                        className={`kyc-feature-card${
                                                            formik.values
                                                                .is_ongoing_aml_enabled
                                                                ? " is-on"
                                                                : ""
                                                        }`}
                                                    >
                                                        <span className="kyc-feature-label">
                                                            Ongoing AML
                                                                {(() => {
                                                                    const info = getPriceForBadge("is_ongoing_aml_enabled");
                                                                    if (!info) return null;

                                                                    if (info.changed) {
                                                                        return (
                                                                            <span className="kyc-plan-price-badge">
                                                                                +{info.text}
                                                                            </span>
                                                                        );
                                                                    }
                                                                    return <PriceBadge price={info.value} />;
                                                                })()}
                                                        </span>
                                                        <Switch
                                                            checked={
                                                                formik.values
                                                                    .is_ongoing_aml_enabled
                                                            }
                                                            onChange={handleToggle(
                                                                "is_ongoing_aml_enabled"
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div
                                                className={`kyc-feature-card${
                                                    formik.values.is_selfie_enabled
                                                        ? " is-on"
                                                        : ""
                                                }`}
                                            >
                                                <span className="kyc-feature-label">
                                                    Selfie Enabled
                                                        {(() => {
                                                            const info = getPriceForBadge("is_selfie_enabled");
                                                            if (!info) return null;

                                                            if (info.changed) {
                                                                return (
                                                                    <span className="kyc-plan-price-badge">
                                                                        +{info.text}
                                                                    </span>
                                                                );
                                                            }
                                                            return <PriceBadge price={info.value} />;
                                                        })()}
                                                </span>
                                                <Switch
                                                    checked={
                                                        formik.values.is_selfie_enabled
                                                    }
                                                    onChange={handleToggle(
                                                        "is_selfie_enabled"
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Portal enabled features</h3>
                                <p>Features available through the customer portal.</p>
                            </div>
                            <div className="fm-card">
                                <div
                                    className={`kyc-feature-tree${
                                        hasFeatureError === "error.main"
                                            ? " is-error"
                                            : ""
                                    }`}
                                >
                                    <div
                                        className={`kyc-feature-card${
                                            formik.values.is_portal_enabled
                                                ? " is-on"
                                                : ""
                                        }`}
                                    >
                                        <span className="kyc-feature-label">
                                            Portal Enabled
                                                {(() => {
                                                    const info = getPriceForBadge("is_portal_enabled");
                                                    if (!info) return null;

                                                    if (info.changed) {
                                                        return (
                                                            <span className="kyc-plan-price-badge">
                                                                +{info.text}
                                                            </span>
                                                        );
                                                    }
                                                    return <PriceBadge price={info.value} />;
                                                })()}
                                        </span>
                                        <Switch
                                            checked={formik.values.is_portal_enabled}
                                            onChange={handleToggle(
                                                "is_portal_enabled"
                                            )}
                                        />
                                    </div>

                                    {formik.values.is_portal_enabled && (
                                        <div className="kyc-feature-nest">
                                            <div
                                                className={`kyc-feature-card${
                                                    formik.values.portal_kyc_enabled
                                                        ? " is-on"
                                                        : ""
                                                }`}
                                            >
                                                <span className="kyc-feature-label">
                                                    KYC Enabled
                                                        {(() => {
                                                            const info = getPriceForBadge("portal_kyc_enabled");
                                                            if (!info) return null;

                                                            if (info.changed) {
                                                                return (
                                                                    <span className="kyc-plan-price-badge">
                                                                        +{info.text}
                                                                    </span>
                                                                );
                                                            }
                                                            return <PriceBadge price={info.value} />;
                                                        })()}
                                                </span>
                                                <Switch
                                                    checked={
                                                        formik.values.portal_kyc_enabled
                                                    }
                                                    onChange={handleToggle(
                                                        "portal_kyc_enabled"
                                                    )}
                                                />
                                            </div>

                                            {formik.values.portal_kyc_enabled && (
                                                <div className="kyc-feature-nest">
                                                    <div
                                                        className={`kyc-feature-card${
                                                            formik.values
                                                                .portal_selfie_enabled
                                                                ? " is-on"
                                                                : ""
                                                        }`}
                                                    >
                                                        <span className="kyc-feature-label">
                                                            Selfie Enabled
                                                                {(() => {
                                                                    const info = getPriceForBadge("portal_selfie_enabled");
                                                                    if (!info) return null;

                                                                    if (info.changed) {
                                                                        return (
                                                                            <span className="kyc-plan-price-badge">
                                                                                +{info.text}
                                                                            </span>
                                                                        );
                                                                    }
                                                                    return <PriceBadge price={info.value} />;
                                                                })()}
                                                        </span>
                                                        <Switch
                                                            checked={
                                                                formik.values
                                                                    .portal_selfie_enabled
                                                            }
                                                            onChange={handleToggle(
                                                                "portal_selfie_enabled"
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div
                                                className={`kyc-feature-card${
                                                    formik.values.portal_aml_enabled
                                                        ? " is-on"
                                                        : ""
                                                }`}
                                            >
                                                <span className="kyc-feature-label">
                                                    AML Enabled
                                                        {(() => {
                                                            const info = getPriceForBadge("portal_aml_enabled");
                                                            if (!info) return null;

                                                            if (info.changed) {
                                                                return (
                                                                    <span className="kyc-plan-price-badge">
                                                                        +{info.text}
                                                                    </span>
                                                                );
                                                            }
                                                            return <PriceBadge price={info.value} />;
                                                        })()}
                                                </span>
                                                <Switch
                                                    checked={
                                                        formik.values.portal_aml_enabled
                                                    }
                                                    onChange={handleToggle(
                                                        "portal_aml_enabled"
                                                    )}
                                                />
                                            </div>

                                            {formik.errors.is_portal_enabled && (
                                                <span className="kyc-feature-error">
                                                    {formik.errors.is_portal_enabled}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Pricing and verifications</h3>
                                <p>Base amounts and included verification counts.</p>
                            </div>
                            <div className="fm-card">
                                <div className="fm-grid">
                                    <div className="fm-field">
                                        <label htmlFor="monthly_base_price">
                                            Monthly Base Price
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="monthly_base_price"
                                            placeholder="Enter monthly base price"
                                            name="monthly_base_price"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={formik.values.monthly_base_price}
                                            onChange={handleNumberChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.monthly_base_price &&
                                                Boolean(
                                                    formik.errors.monthly_base_price
                                                )
                                            }
                                            helperText={
                                                formik.touched.monthly_base_price &&
                                                formik.errors.monthly_base_price
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <span className="kyc-plan-currency">
                                                        $
                                                    </span>
                                                ),
                                            }}
                                        />
                                    </div>
                                    <div className="fm-field">
                                        <label htmlFor="monthly_included_verification">
                                            Monthly Included Verifications
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="monthly_included_verification"
                                            placeholder="Enter monthly included verifications"
                                            name="monthly_included_verification"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={
                                                formik.values
                                                    .monthly_included_verification
                                            }
                                            onChange={handleNumberChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched
                                                    .monthly_included_verification &&
                                                Boolean(
                                                    formik.errors
                                                        .monthly_included_verification
                                                )
                                            }
                                            helperText={
                                                formik.touched
                                                    .monthly_included_verification &&
                                                formik.errors
                                                    .monthly_included_verification
                                            }
                                        />
                                    </div>
                                    <div className="fm-field">
                                        <label htmlFor="yearly_base_price">
                                            Yearly Base Price
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="yearly_base_price"
                                            placeholder="Enter yearly base price"
                                            name="yearly_base_price"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={formik.values.yearly_base_price}
                                            onChange={handleNumberChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.yearly_base_price &&
                                                Boolean(
                                                    formik.errors.yearly_base_price
                                                )
                                            }
                                            helperText={
                                                formik.touched.yearly_base_price &&
                                                formik.errors.yearly_base_price
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <span className="kyc-plan-currency">
                                                        $
                                                    </span>
                                                ),
                                            }}
                                        />
                                    </div>
                                    <div className="fm-field">
                                        <label htmlFor="yearly_included_verification">
                                            Yearly Included Verifications
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="yearly_included_verification"
                                            placeholder="Enter yearly included verifications"
                                            name="yearly_included_verification"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={
                                                formik.values
                                                    .yearly_included_verification
                                            }
                                            onChange={handleNumberChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched
                                                    .yearly_included_verification &&
                                                Boolean(
                                                    formik.errors
                                                        .yearly_included_verification
                                                )
                                            }
                                            helperText={
                                                formik.touched
                                                    .yearly_included_verification &&
                                                formik.errors
                                                    .yearly_included_verification
                                            }
                                        />
                                    </div>
                                    <div className="fm-field">
                                        <label htmlFor="per_verification_price">
                                            Per Verifications Price
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="per_verification_price"
                                            placeholder="Enter per verifications price"
                                            name="per_verification_price"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={
                                                formik.values.per_verification_price
                                            }
                                            onChange={handleNumberChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched
                                                    .per_verification_price &&
                                                Boolean(
                                                    formik.errors
                                                        .per_verification_price
                                                )
                                            }
                                            helperText={
                                                formik.touched
                                                    .per_verification_price &&
                                                formik.errors.per_verification_price
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <span className="kyc-plan-currency">
                                                        $
                                                    </span>
                                                ),
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Add-ons pricing</h3>
                                <p>Optional AML add-ons and unit prices.</p>
                            </div>
                            <div className="fm-card">
                                <div className="kyc-addon-list">
                                    <div className="kyc-addon-row">
                                        <span className="kyc-addon-name">
                                            AML Ongoing Check
                                                {(() => {
                                                    const info = getPriceForBadge("aml_ongoing_check");
                                                    if (!info) return null;

                                                    if (info.changed) {
                                                        return (
                                                            <span className="kyc-plan-price-badge">
                                                                +{info.text}
                                                            </span>
                                                        );
                                                    }
                                                    return <PriceBadge price={info.value} />;
                                                })()}
                                        </span>
                                        <div className="kyc-addon-enabled">
                                            <Switch
                                                checked={
                                                    formik.values.addons
                                                        ?.aml_ongoing_check
                                                        ?.is_enabled
                                                }
                                                onChange={(e) => {
                                                    const checked = e.target.checked;

                                                    formik.setFieldValue(
                                                        "addons.aml_ongoing_check.is_enabled",
                                                        checked
                                                    );

                                                    if (!checked) {
                                                        formik.setFieldValue(
                                                            "addons.aml_ongoing_check.unit_price",
                                                            ""
                                                        );
                                                    } else {
                                                        formik.setFieldTouched(
                                                            "addons.aml_ongoing_check.unit_price",
                                                            true
                                                        );
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="fm-field kyc-addon-price">
                                            <TextField
                                                name="addons.aml_ongoing_check.unit_price"
                                                size="small"
                                                type="number"
                                                fullWidth
                                                placeholder="Unit price"
                                                disabled={
                                                    !formik.values.addons
                                                        ?.aml_ongoing_check
                                                        ?.is_enabled
                                                }
                                                value={
                                                    formik.values.addons
                                                        ?.aml_ongoing_check
                                                        ?.is_enabled
                                                        ? formik.values.addons
                                                              ?.aml_ongoing_check
                                                              ?.unit_price
                                                        : ""
                                                }
                                                onChange={(e) => {
                                                    formik.setFieldValue(
                                                        "addons.aml_ongoing_check.unit_price",
                                                        e.target.value
                                                    );
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched.addons
                                                        ?.aml_ongoing_check
                                                        ?.unit_price &&
                                                    Boolean(
                                                        formik.errors.addons
                                                            ?.aml_ongoing_check
                                                            ?.unit_price
                                                    )
                                                }
                                                helperText={
                                                    formik.touched.addons
                                                        ?.aml_ongoing_check
                                                        ?.unit_price &&
                                                    formik.errors.addons
                                                        ?.aml_ongoing_check
                                                        ?.unit_price
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className="kyc-addon-row">
                                        <span className="kyc-addon-name">
                                            AML Match
                                                {(() => {
                                                    const info = getPriceForBadge("aml_match");
                                                    if (!info) return null;

                                                    if (info.changed) {
                                                        return (
                                                            <span className="kyc-plan-price-badge">
                                                                +{info.text}
                                                            </span>
                                                        );
                                                    }
                                                    return <PriceBadge price={info.value} />;
                                                })()}
                                        </span>
                                        <div className="kyc-addon-enabled">
                                            <Switch
                                                checked={
                                                    formik.values.addons?.aml_match
                                                        ?.is_enabled
                                                }
                                                onChange={(e) => {
                                                    const checked = e.target.checked;

                                                    formik.setFieldValue(
                                                        "addons.aml_match.is_enabled",
                                                        checked
                                                    );

                                                    if (!checked) {
                                                        formik.setFieldValue(
                                                            "addons.aml_match.unit_price",
                                                            ""
                                                        );
                                                    } else {
                                                        formik.setFieldTouched(
                                                            "addons.aml_match.unit_price",
                                                            true
                                                        );
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="fm-field kyc-addon-price">
                                            <TextField
                                                name="addons.aml_match.unit_price"
                                                size="small"
                                                type="number"
                                                fullWidth
                                                placeholder="Unit price"
                                                disabled={
                                                    !formik.values.addons?.aml_match
                                                        ?.is_enabled
                                                }
                                                value={
                                                    formik.values.addons?.aml_match
                                                        ?.is_enabled
                                                        ? formik.values.addons
                                                              ?.aml_match?.unit_price
                                                        : ""
                                                }
                                                onChange={(e) => {
                                                    formik.setFieldValue(
                                                        "addons.aml_match.unit_price",
                                                        e.target.value
                                                    );
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched.addons?.aml_match
                                                        ?.unit_price &&
                                                    Boolean(
                                                        formik.errors.addons
                                                            ?.aml_match?.unit_price
                                                    )
                                                }
                                                helperText={
                                                    formik.touched.addons?.aml_match
                                                        ?.unit_price &&
                                                    formik.errors.addons?.aml_match
                                                        ?.unit_price
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
{/* 
                        <div className="kyc-plan-charges">
                            <div className="kyc-plan-charges-copy">
                                <span className="kyc-plan-charges-icon" aria-hidden>
                                    <ShieldCheck size={18} />
                                </span>
                                <div>
                                    <strong>Extra Charges</strong>
                                    <p>This amount will be charged</p>
                                </div>
                            </div>
                            <div className="kyc-plan-charges-amount">
                                $ {totalPrice.toFixed(2)}
                            </div>
                        </div> */}
                            <div className="kyc-plan-charges">

                                <div className="kyc-plan-charges-copy">
                                    <span className="kyc-plan-charges-icon" aria-hidden>
                                        <ShieldCheck size={18} />
                                    </span>

                                    <div>
                                        <strong>Extra Charges</strong>

                                        <p>
                                            {isExtraChargeUpdated
                                                ? "This amount will be applicable after the plan is updated"
                                                : "This amount will be charged based on selected features"}
                                        </p>
                                    </div>
                                </div>

                                <div className="kyc-plan-charges-amount-wrapper">

                                    {isExtraChargeUpdated && (
                                        <div className="kyc-plan-charges-previous">
                                            <span className="old-price">
                                                Was: $ {Number(planData.isExtraCharges).toFixed(2)}
                                            </span>

                                            <span className="arrow">
                                                →
                                            </span>
                                        </div>
                                    )}

                                    <div className="kyc-plan-charges-amount">
                                        $ {totalPrice.toFixed(2)}
                                    </div>
                                </div>

                            </div>
                    </div>

                    <div className="fm-footer">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="fm-btn"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="fm-btn is-primary"
                            disabled={loading}
                        >
                            {id ? "Update" : "Create"} Plan
                            {loading && (
                                <CircularProgress size={16} color="inherit" />
                            )}
                        </button>
                    </div>
                </form>
            )}
        </Dialog>
    );
};

export default AddEditKycPlan;
